import { useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import { Loading } from "../../componentes/loading/loading";
import { useConfiguracaoAprovacaoFilial } from "../../hooks/solicitacoes/useConfiguracaoAprovacaoFilial";
import { useAtribuicoesConfig } from "../../hooks/solicitacoes/useAtribuicoesConfig";
import { PROCESSO_REGISTRY, processoInfo } from "../../utils/solicitacoes/processoRegistry";

const PROCESSO_OPTIONS = Object.entries(PROCESSO_REGISTRY).map(([value, info]) => ({ value, label: info.label }));

// Administra a tabela Processo × Filial × Etapa → Grupo que alimenta o tipo de atribuição
// GRUPO_DA_FILIAL (aba "Atribuição de Etapas") — uma coluna por etapa do processo que estiver
// configurada com esse tipo, para que cada etapa possa ter seu próprio grupo por filial.
export default function FilialAprovacaoConfigTab() {
    const [tipoProcesso, setTipoProcesso] = useState("SOLICITACAO_COMPRAS");
    const { configs, filiais, grupos, loading, saving, salvarFilial, excluirFilial } = useConfiguracaoAprovacaoFilial(tipoProcesso);
    const { configs: atribConfigs, loading: loadingAtribuicoes } = useAtribuicoesConfig();
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [form, setForm] = useState({ filialId: null, porEtapa: {} });
    const [editando, setEditando] = useState(false);
    const [paraExcluir, setParaExcluir] = useState(null);

    const processoLabel = processoInfo(tipoProcesso)?.label || tipoProcesso;

    // Colunas dinâmicas: só as etapas deste processo que já estão configuradas com o tipo de
    // atribuição GRUPO_DA_FILIAL na aba "Atribuição de Etapas" — nada fica hardcoded aqui.
    const colunas = useMemo(() => {
        const codigosComFilial = new Set(
            atribConfigs.filter(c => c.tipoProcesso === tipoProcesso && c.tipoAtribuicao === "GRUPO_DA_FILIAL")
                .map(c => c.etapaCodigo)
        );
        return (processoInfo(tipoProcesso)?.etapas || []).filter(e => codigosComFilial.has(e.codigo));
    }, [atribConfigs, tipoProcesso]);

    // Pivota as linhas (uma por processo+filial+etapa) numa linha por filial, com um valor por coluna/etapa.
    const linhasPorFilial = useMemo(() => {
        const mapa = new Map();
        for (const c of configs) {
            if (!mapa.has(c.filialId)) {
                mapa.set(c.filialId, { filialId: c.filialId, filialNome: c.filialNome, filialCodigo: c.filialCodigo, emUso: c.emUso, porEtapa: {} });
            }
            mapa.get(c.filialId).porEtapa[c.etapaCodigo] = c;
        }
        return [...mapa.values()].sort((a, b) => a.filialNome.localeCompare(b.filialNome));
    }, [configs]);

    // Ao vincular uma filial nova, só oferece as que ainda não têm nenhuma configuração para este
    // processo — evita duplicidade na origem em vez de rejeitar depois de tentar salvar.
    const filiaisDisponiveis = useMemo(() => {
        if (editando) return filiais;
        const jaConfiguradas = new Set(linhasPorFilial.map(l => l.filialId));
        return filiais.filter(f => !jaConfiguradas.has(f.id));
    }, [filiais, linhasPorFilial, editando]);

    const temAlgumGrupoSelecionado = colunas.some(col => form.porEtapa[col.codigo]?.grupoAprovacaoId);

    function abrirModal(linha = null) {
        if (linha) {
            const porEtapa = {};
            colunas.forEach(col => {
                const config = linha.porEtapa[col.codigo];
                porEtapa[col.codigo] = { id: config?.id ?? null, grupoAprovacaoId: config?.grupoAprovacaoId ?? null };
            });
            setForm({ filialId: linha.filialId, porEtapa });
            setEditando(true);
        } else {
            setForm({ filialId: null, porEtapa: {} });
            setEditando(false);
        }
        setModalOpen(true);
    }

    function fecharModal() {
        setModalOpen(false);
    }

    function setGrupoDaEtapa(etapaCodigo, grupoId) {
        setForm(prev => ({
            ...prev,
            porEtapa: { ...prev.porEtapa, [etapaCodigo]: { ...(prev.porEtapa[etapaCodigo] || { id: null }), grupoAprovacaoId: grupoId } },
        }));
    }

    function salvarForm(e) {
        e.preventDefault();
        const itens = colunas.map(col => ({
            id: form.porEtapa[col.codigo]?.id ?? null,
            etapaCodigo: col.codigo,
            grupoAprovacaoId: form.porEtapa[col.codigo]?.grupoAprovacaoId ?? null,
        }));
        salvarFilial(form.filialId, itens).then(fecharModal).catch(() => {});
    }

    function confirmarExclusao(linha) {
        setParaExcluir(linha);
        setDeleteModalOpen(true);
    }

    function excluirConfirmado() {
        const ids = colunas.map(col => paraExcluir.porEtapa[col.codigo]?.id).filter(Boolean);
        excluirFilial(ids).then(() => setDeleteModalOpen(false)).catch(() => {});
    }

    const carregando = loading || loadingAtribuicoes;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5 max-w-xs">
                <label className="text-sm font-medium text-foreground/80">Processo</label>
                <CustomDropdown
                    value={tipoProcesso}
                    onChange={e => setTipoProcesso(e.value)}
                    options={PROCESSO_OPTIONS}
                    placeholder="Selecione"
                />
            </div>

            {!carregando && colunas.length === 0 ? (
                <div className="rounded-xl border border-border bg-muted px-6 py-8 text-center text-sm text-muted-foreground">
                    Nenhuma etapa de {processoLabel} está configurada com o tipo de atribuição "Grupo da Filial".
                    Configure isso primeiro na aba "Atribuição de Etapas" para poder vincular filiais aqui.
                </div>
            ) : (
                <>
                    <div className="flex justify-end">
                        <button
                            onClick={() => abrirModal()}
                            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:brightness-95"
                        >
                            <i className="fas fa-plus mr-2" /> Vincular Filial
                        </button>
                    </div>

                    {carregando ? (
                        <Loading />
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-border fade-in">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-table-header">
                                    <tr className="text-sm">
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Filial</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Código</th>
                                        {colunas.map(col => (
                                            <th key={col.codigo} className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">{col.label}</th>
                                        ))}
                                        <th className="py-3 px-6 text-right text-xs font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {linhasPorFilial.length === 0 ? (
                                        <tr>
                                            <td colSpan={colunas.length + 3} className="px-6 py-4 text-center text-muted-foreground">
                                                Nenhuma filial vinculada para {processoLabel}
                                            </td>
                                        </tr>
                                    ) : (
                                        linhasPorFilial.map(linha => (
                                            <tr key={linha.filialId} className="text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground">{linha.filialNome}</td>
                                                <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{linha.filialCodigo}</td>
                                                {colunas.map(col => (
                                                    <td key={col.codigo} className="px-6 py-3 whitespace-nowrap text-foreground">
                                                        {linha.porEtapa[col.codigo]?.grupoAprovacaoNome || <span className="text-muted-foreground">—</span>}
                                                    </td>
                                                ))}
                                                <td className="px-3 py-3 whitespace-nowrap text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <i onClick={() => abrirModal(linha)} className="fa fa-pencil-square text-primary hover:text-primary/80 p-1 rounded-full hover:bg-primary/10 cursor-pointer" />
                                                        <i onClick={() => confirmarExclusao(linha)} className="fa fa-times text-destructive hover:text-destructive/80 p-1 rounded-full hover:bg-destructive/10 cursor-pointer" />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <Dialog
                header={editando ? "Editar Configuração" : "Vincular Filial"}
                onHide={fecharModal}
                visible={modalOpen}
                blockScroll
                className="w-[95%] max-w-lg"
                footer={
                    <div className="flex justify-end space-x-3 mt-3">
                        <button type="button" onClick={fecharModal} className="px-4 py-2 bg-card border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                        <button
                            type="submit"
                            onClick={salvarForm}
                            disabled={saving || !form.filialId || !temAlgumGrupoSelecionado}
                            className="px-4 py-2 bg-primary hover:brightness-95 text-primary-foreground rounded-md transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <i className="fas fa-spinner fa-spin" />}
                            {saving ? "Salvando..." : "Salvar"}
                        </button>
                    </div>
                }
            >
                <form className="p-4 flex flex-col gap-4" onSubmit={salvarForm}>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Processo</label>
                        <div className="h-9 w-full flex items-center px-3 text-sm border border-input rounded-md bg-muted text-muted-foreground">
                            {processoLabel}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Filial <span className="text-destructive">*</span></label>
                        <CustomDropdown
                            value={form.filialId}
                            onChange={e => setForm(prev => ({ ...prev, filialId: e.value }))}
                            options={filiaisDisponiveis}
                            optionValue="id"
                            optionLabel="nome"
                            placeholder={filiaisDisponiveis.length ? "Selecione" : "Todas as filiais já possuem configuração"}
                            disabled={editando}
                            filter
                        />
                    </div>

                    {colunas.map(col => (
                        <div key={col.codigo} className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Grupo — {col.label}</label>
                            <CustomDropdown
                                value={form.porEtapa[col.codigo]?.grupoAprovacaoId ?? null}
                                onChange={e => setGrupoDaEtapa(col.codigo, e.value)}
                                options={grupos}
                                optionValue="id"
                                optionLabel="nome"
                                placeholder="Não configurado"
                                showClear
                                filter
                            />
                        </div>
                    ))}
                </form>
            </Dialog>

            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">
                            Deseja realmente remover todas as configurações de etapa da filial {paraExcluir?.filialNome} ({paraExcluir?.filialCodigo}) para o processo {processoLabel}?
                        </p>
                        {paraExcluir?.emUso && (
                            <p className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                                <i className="fa fa-exclamation-triangle mr-1" />
                                Atenção: existem solicitações em andamento para esta filial neste processo. Uma solicitação futura poderá ficar sem responsável até que uma nova configuração seja cadastrada.
                            </p>
                        )}
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setDeleteModalOpen(false)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={excluirConfirmado} className="px-4 py-2 rounded-md transition-all duration-150 bg-destructive text-destructive-foreground hover:brightness-95">Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
